'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { Theme, EmojiStyle } from 'emoji-picker-react';
import { X } from 'lucide-react';

const Picker = dynamic(() => import('emoji-picker-react'), { ssr: false });

type Props = {
  anchorRef: React.RefObject<HTMLElement>;
  onSelect: (emoji: string) => void;
  onClose: () => void;
};

const PICKER_W = 320;
const PICKER_H = 380;

/**
 * Anchored emoji popover that renders into document.body via a portal so it
 * cannot be clipped by a composer ancestor's `overflow: hidden`. On mobile
 * (<640px) it falls back to a full-width bottom sheet that respects the
 * iOS home-indicator safe area.
 *
 * The inner Picker is wrapped in a CSS-isolation container so Tailwind's
 * preflight (`img { max-width: 100% }`, `button { background: transparent }`,
 * etc.) cannot break the picker's internal sprite/skin-tone layout. Using
 * EmojiStyle.NATIVE renders Unicode glyphs directly and sidesteps the
 * sprite-overlap bug entirely on browsers that have system emoji.
 *
 * Closes on Escape, click-outside, or selection.
 */
export default function EmojiPickerPopover({ anchorRef, onSelect, onClose }: Props) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ left: number; bottom: number } | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const measure = () => {
      if (typeof window === 'undefined') return;
      const mobile = window.matchMedia('(max-width: 640px)').matches;
      setIsMobile(mobile);
      if (mobile) {
        setCoords(null);
        return;
      }
      const trigger = anchorRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const left = Math.min(
        Math.max(8, r.left),
        window.innerWidth - PICKER_W - 8,
      );
      const bottom = window.innerHeight - r.top + 8;
      setCoords({ left, bottom });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handlePointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [anchorRef, onClose]);

  if (typeof document === 'undefined') return null;

  // Reset wrapper that re-asserts the picker's expected layout primitives
  // wherever Tailwind preflight has reset them.
  const isolateClass = 'dm-emoji-isolate';

  const pickerEl = (width: number | string) => (
    <div
      className={isolateClass}
      style={{
        // `isolation: isolate` keeps any z-index inside the picker (skin-tone
        // tray, category dropdown) layered correctly within the popover.
        isolation: 'isolate',
        width,
      }}
    >
      <Picker
        onEmojiClick={(d) => {
          onSelect(d.emoji);
          onClose();
        }}
        theme={Theme.DARK}
        emojiStyle={EmojiStyle.NATIVE}
        lazyLoadEmojis
        autoFocusSearch={false}
        previewConfig={{ showPreview: false }}
        width={width}
        height={PICKER_H}
      />
      {/* Scoped CSS reset: undo Tailwind preflight side-effects that the
          picker's internal stylesheet would otherwise have to fight. The
          attribute selector keeps these rules from leaking outside this
          wrapper.

          NOTE: we deliberately do NOT reset `button` backgrounds here. The
          picker paints its functional controls with backgrounds — the
          skin-tone swatches are colored via `background-color`
          (.epr-tone-*) and the category-nav icons via a
          `background-image` sprite (.epr-cat-btn). A blanket
          `button { background: transparent; background-image: none }` blanked
          both (the empty square beside the search field was the skin-tone
          swatch). The picker's own class rules already override preflight's
          element-level `button` reset, so no button reset is needed. */}
      <style jsx>{`
        .${isolateClass} :global(img),
        .${isolateClass} :global(.epr-emoji-img) {
          max-width: none;
          height: auto;
          display: inline-block;
        }
        .${isolateClass} :global(ul) {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        /* Neutralizes ONLY the app-wide 44px touch-target floor from
           globals.css (\`button { min-height: 44px; min-width: 44px }\`), so the
           library's own explicit sizing governs its internal controls.

           This is NOT the background reset that was removed on 2026-07-05 — do
           not delete it as residue. It fixes a different bug by a different
           mechanism: min-height/min-width are separate properties from
           height/width, so the floor wins on used-size regardless of
           specificity. The category nav is a 10x4 sprite with 30x30 cells
           positioned by background-position math that assumes a 30px button;
           at 44px each button bled 14px of the next row AND column, rendering
           two overlapping rows of icons. The same floor stretched the skin-tone
           swatch (border-radius 4px, absolute, in a 28px-tall container) into a
           clipped solid square. */
        .${isolateClass} :global(button) {
          min-height: 0;
          min-width: 0;
        }
      `}</style>
    </div>
  );

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[80]">
        <div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
          aria-hidden
        />
        <div
          ref={popoverRef}
          className="absolute left-0 right-0 bottom-0 bg-surface-1 border-t border-border-default rounded-t-2xl shadow-2xl"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          role="dialog"
          aria-label="Emoji picker"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-border-subtle">
            <span className="text-sm font-medium text-text-primary">Emoji</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close emoji picker"
              className="p-1.5 rounded-sm text-text-secondary hover:text-text-primary hover:bg-surface-2"
            >
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
          {pickerEl('100%')}
        </div>
      </div>,
      document.body,
    );
  }

  if (!coords) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[80] rounded-md shadow-2xl border border-border-default bg-surface-1"
      style={{ left: coords.left, bottom: coords.bottom, width: PICKER_W }}
      role="dialog"
      aria-label="Emoji picker"
    >
      {pickerEl(PICKER_W)}
    </div>,
    document.body,
  );
}
